import type { Paragraph, Table } from "docx";
import { SKRData, SKRFraga, SKR_SCALE, skrLabel, skrLabelWithScore, skrScore } from "@/types/skr";
import { downloadWordDocument } from "@/lib/documentGenerator/exportHistory";
import { createStampForCurrentUser } from "@/lib/documentGenerator/watermark";
import {
  body,
  buildDocument,
  factTable,
  h1,
  signatureBlock,
  table,
} from "@/lib/documentGenerator/docTheme";
import { documentFooter } from "@/lib/documentGenerator/documentFooter";

export interface SKRExportOptions {
  includeProjectInfo?: boolean;
  includeAssessments?: boolean;
  includeSignatures?: boolean;
  includeSummary?: boolean;
}


export const generateSKRWordDocument = async (
  skrData: SKRData,
  allQuestions: SKRFraga[] = [],
  options: SKRExportOptions = {}
): Promise<void> => {
  const {
    includeProjectInfo = true,
    includeAssessments = true,
    includeSignatures = true,
    includeSummary = true,
  } = options;

  const groupedQuestions = allQuestions.reduce((acc, fraga) => {
    (acc[fraga.kategori] ||= []).push(fraga);
    return acc;
  }, {} as Record<string, SKRFraga[]>);

  const categoryAssessments = Object.keys(groupedQuestions).map((kategori) => {
    const questions = groupedQuestions[kategori];
    const answered = questions.filter((q) => skrData.svar[q.id]?.svar);
    const scores = answered
      .map((q) => skrScore(skrData.svar[q.id].svar))
      .filter((n): n is number => n !== null);
    const avg = scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : 0;
    const predominantLevel =
      scores.length > 0
        ? `${skrLabel(SKR_SCALE[Math.min(4, Math.max(0, Math.round(avg) - 1))].value)} (${avg.toFixed(1)})`
        : "ej bedömd";

    return {
      kategori,
      totalQuestions: questions.length,
      answered: answered.length,
      predominantLevel: answered.length > 0 ? predominantLevel : "ej bedömd",
    };
  });

  const documentName = "SKR-bedömning";
  const stamp = await createStampForCurrentUser({
    projectName: skrData.projektNamn || "",
    docNumber: "03",
    documentName,
  });

  const children: (Paragraph | Table)[] = [
    body("Storlek, komplexitet och risk – underlag för hur projektets arbetsmiljöarbete ska dimensioneras.", {
      italics: true,
      muted: true,
    }),
  ];

  let section = 1;

  if (includeProjectInfo) {
    children.push(
      h1(`${section++}. Projektinformation`),
      factTable([
        ["Projektnamn", skrData.projektNamn],
        ["Beskrivning", skrData.projektbeskrivning],
        ["Budget", skrData.budget],
        ["Byggtid", skrData.byggtid],
      ])
    );
  }

  if (includeSummary) {
    children.push(
      h1(`${section++}. Sammanfattning av bedömning`),
      table(
        ["Kategori", "Bedömning", "Besvarade/Totalt", "Status"],
        categoryAssessments.map((c) => [
          c.kategori,
          c.predominantLevel,
          `${c.answered}/${c.totalQuestions}`,
          c.answered === c.totalQuestions ? "Komplett" : "Ofullständig",
        ]),
        { columnWidths: [3800, 1900, 1900, 1760] }
      )
    );
  }

  if (includeAssessments) {
    children.push(h1(`${section++}. Bedömning per kategori`));
    Object.keys(groupedQuestions).forEach((kategori) => {
      children.push(
        h1(kategori),
        table(
          ["Fråga", "Bedömning", "Kommentar", "Åtgärd"],
          groupedQuestions[kategori].map((fraga) => {
            const svar = skrData.svar[fraga.id];
            return [
              fraga.hjalptext ? `${fraga.text}\n${fraga.hjalptext}` : fraga.text,
              skrLabelWithScore(svar?.svar),
              svar?.kommentar,
              svar?.atgard,
            ];
          }),
          { columnWidths: [4200, 1400, 2200, 1560] }
        )
      );
    });
  }

  if (includeSignatures) {
  }
  const footer = await documentFooter({ docNumber: "03", documentType: "skr", signatureRoles: ["Upprättad av", "Byggherre", "Bas-P"] });

  const doc = buildDocument({
    meta: {
      status: footer.status,
      version: footer.version,
      review: footer.review,
      docNumber: "03",
      documentName,
      projectName: skrData.projektNamn || "",
    },
    stamp,
    children: [...children, ...footer.children],
  });

  await downloadWordDocument(doc, `SKR-bedömning_${skrData.projektNamn || "Projekt"}.docx`, {
    documentType: "skr",
    docNumber: "03",
    documentName: `SKR-bedömning ${skrData.projektNamn || ""}`.trim(),
    snapshot: skrData,
    stamp,
  });
};
